import type { EstadoConexion } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { conexionesApi } from '../api/conexiones.api';

// Conexiones externas (01/10/2026): el 29/09 el token de Buffer venció y nadie
// se enteró en 2 días porque el chequeo automático no tenía a dónde avisar.
// Esta pantalla prueba cada conexión de verdad; la campanita avisa si alguna cae.

function fecha(iso: string | null, nunca: string): string {
  if (!iso) return nunca;
  return new Date(iso).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
}

function Badge({ estado, texto }: { estado: EstadoConexion; texto: string }): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
        estado === 'OK' && 'bg-success/15 text-success',
        estado === 'ERROR' && 'bg-danger/15 text-danger',
        estado === 'SIN_PROBAR' && 'bg-marca-neutro text-marca-gris/70',
      )}
    >
      {texto}
    </span>
  );
}

export function ConexionesPage(): JSX.Element {
  const es = useI18n();
  const t = es.conexiones;
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: qk.conexiones.all,
    queryFn: () => conexionesApi.listar(),
  });

  const probarMutation = useMutation({
    mutationFn: () => conexionesApi.probar(),
    onSuccess: (resultado) => {
      queryClient.setQueryData(qk.conexiones.all, resultado);
      toast.success(t.exito);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  const conexiones = data?.conexiones ?? [];
  const conError = conexiones.filter((c) => c.estado === 'ERROR').length;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {conError > 0 && <Badge estado="ERROR" texto={`${String(conError)}`} />}
        </div>
        <Button size="sm" onClick={() => probarMutation.mutate()} disabled={probarMutation.isPending}>
          <RefreshCw size={14} className={cn(probarMutation.isPending && 'animate-spin')} />
          {probarMutation.isPending ? t.probando : t.probar}
        </Button>
      </div>

      {error && (
        <Alert variant="error" className="mt-4">
          {getErrorMessage(error, es.errores.generico.algoSalioMal)}
        </Alert>
      )}
      {isLoading && <p className="mt-6 text-sm text-text-subtle">{es.app.cargando}</p>}

      {data && (
        <div className="mt-4 overflow-x-auto rounded-md border border-marca-neutro bg-surface">
          <table className="w-full text-sm">
            <thead className="border-b border-marca-neutro bg-marca-neutro/30 text-left text-[11px] uppercase text-marca-gris/70">
              <tr>
                <th className="px-3 py-2">{t.columnas.conexion}</th>
                <th className="px-3 py-2">{t.columnas.estado}</th>
                <th className="px-3 py-2">{t.columnas.ultimoUso}</th>
                <th className="px-3 py-2">{t.columnas.ultimaPrueba}</th>
                <th className="px-3 py-2">{t.columnas.detalle}</th>
              </tr>
            </thead>
            <tbody>
              {conexiones.map((c) => (
                <tr key={c.codigo} className="border-b border-marca-neutro/40 last:border-0">
                  <td className="px-3 py-2 font-medium text-text-strong">{t.nombres[c.codigo]}</td>
                  <td className="px-3 py-2">
                    <Badge estado={c.estado} texto={t.estados[c.estado]} />
                  </td>
                  <td className="px-3 py-2 text-marca-gris/70">{fecha(c.ultimoUsoExitoso, t.nunca)}</td>
                  <td className="px-3 py-2 text-marca-gris/70">{fecha(c.ultimaProbada, t.nunca)}</td>
                  <td className="px-3 py-2 text-marca-gris/70">
                    {c.estado === 'ERROR' && c.detalleError ? c.detalleError : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
